<template>
    <div class="tw-p-[15px] tw-rounded-[8px] tw-bg-white">
        <div class="tw-rounded-[8px] tw-overflow-hidden tw-border tw-border-solid tw-border-[#dddddd]">
            <div :class="[timeDiff === 0 ? 'bg--change': '', 'tw-bg-[#E4E8FF] tw-px-[15px] tw-py-[10px] tw-mb-[10px]']">
                <div class='tw-flex tw-justify-between'>
                    <div class="tw-font-semibold tw-text-[#0D3659]">
                        <span>{{ delivery.items_name + ' - #' + delivery.id }}</span>
                    </div>

                    <div
                      v-if="delivery.canCustomerViewHelperReviews"
                      @click="openDeliveryReviewModal"
                      class="tw-block tw-px-[15px] tw-py-[3px] tw-bg-white tw-text-[#0D3659] tw-font-semibold tw-text-[14px] tw-rounded-[8px] tw-cursor-pointer"
                    >
                      <span>See Company Reviews</span>
                    </div>

                    <div
                      v-if="!delivery.canCustomerViewHelperReviews && !isCancelledDeliveryOrder(delivery)"
                      class="tw-block tw-px-[15px] tw-py-[3px] tw-bg-white tw-text-[#0D3659] tw-font-semibold tw-text-[14px] tw-rounded-[8px] tw-cursor-pointer"
                    >
                      <span>Matching in Progress</span>
                    </div>

                    <div
                      v-if="isCancelledDeliveryOrder(delivery)"
                      class="tw-block tw-px-[15px] tw-py-[3px] tw-bg-white tw-text-[#0D3659] tw-font-semibold tw-text-[14px] tw-rounded-[8px] tw-cursor-pointer"
                    >
                      <span>Unmatched – Cancelled</span>
                    </div>
                </div>
            </div>

            <v-card outlined tile class="rounded-b dashboard-card__main">
                <div class="tw-bg-[#F4F6FF]">
                    <div
                        class="tw-w-full tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-px-[15px] tw-py-[8px]">
                        <div class="tw-flex tw-items-center tw-gap-[10px] tw-font-bold tw-text-[#0D3659]">
                            <v-tooltip
                              v-if="delivery.status === 'Expired – No Company Accepted'"
                              top
                              content-class="charge-text__tooltip"
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <div class="tw-flex tw-gap-[8px]" v-bind="attrs" v-on="on">
                                  <i class="icony icony-info-circle-o tw-text-secondary"></i>
                                  <span>{{ delivery.status }}</span>
                                </div>
                              </template>
                              <span class="custom--v-tooltip">
                                No company accepted your delivery. Please press the 'Cancel' button to cancel the order and receive a full refund automatically
                              </span>
                            </v-tooltip>

                            <span v-else v-text="delivery.status"/>
                        </div>
                        <button type="button"
                                @click="openDeliveryDetailsModal(delivery.id)"
                                class="tw-bg-[white] tw-border tw-border-solid tw-border-[#E3E8FF] tw-text-[14px] tw-p-[2px_12px] tw-rounded-[8px] tw-text-[#0D3659] tw-font-semibold hover:tw-bg-[#0D3659] hover:tw-border-[#0D3659] tw-duration-300 hover:tw-text-[#FFF]">
                            {{ isCardOpened ? "Close details" : "View details" }}
                        </button>
                    </div>
                </div>

                <div class="tw-p-[15px_15px_0_15px]">
                  <div class="tw-flex tw-justify-between tw-gap-[35px] tw-pb-[15px] tw-border-0 tw-border-b tw-border-solid tw-border-[#DDDDDD]">
                    <div class="tw-flex tw-gap-[35px]">
                      <div
                        v-if="getVehicle"
                        class="tw-flex tw-items-center tw-justify-between tw-gap-[5px]"
                      >
                        <img :src="getVehicle.image" alt="" class="tw-h-[18px] tw-w-auto">
                        <span class="tw-text-[16px] tw-text-[#0D3659] tw-font-semibold tw-whitespace-nowrap"> -
                          {{ getVehicle.name }}
                        </span>
                      </div>

                      <div
                        class="tw-flex tw-items-center tw-text-[#0D3659] tw-font-semibold"
                      >
                        <i class="icony icony-mapmarker-clock tw-mr-[8px] tw-text-[#8F3985]"></i>
                        <span>{{ delivery.pickup }}</span>
                      </div>

                      <div
                        v-if="delivery.destination"
                        class="tw-flex tw-items-center tw-text-[#0D3659] tw-font-semibold"
                      >
                        <i class="icony icony-mapmarker-check tw-mr-[8px] tw-text-[#8F3985]"></i>
                        <span>{{ delivery.destination }}</span>
                      </div>
                    </div>
                    <div
                      class="tw-text-[#0D3659] tw-font-semibold"
                      v-if="delivery.acceptor.phone"
                    >
                      Helper phone: {{ delivery.acceptor.phone }}
                    </div>
                  </div>
                </div>

                <div class="tw-p-[15px]">
                    <user-dashboard-delivery-card-footer :delivery="delivery"/>
                </div>
            </v-card>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import UserDashboardDeliveryCardDetailsModal from "@/views/UserDashboard/UserDashboardDeliveryCardDetailsModal";
import UserDashboardDeliveryCardFooter from "@/views/UserDashboard/UserDashboardDeliveryCardFooter.vue";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { convertSecondToTime } from "@/plugins/helpers.js";
import { EventBus } from '@/main';
import { DASHBOARD_URL } from '@/plugins/consts';
import DeliveryReviews from '@/components/Modals/DeliveryReviews.vue';
import { isCancelledDeliveryOrder } from '@/plugins/delivery_order_methods';

dayjs.extend(utc);
dayjs.extend(timezone);

export default {
    components: {
        UserDashboardDeliveryCardDetailsModal,
        UserDashboardDeliveryCardFooter
    },

    props: {
        delivery: {
            type: Object,
            required: true,
        }
    },

    data() {
        return {
            isCardOpened: false,
            timeDiff: 0,
            interval: null,
        }
    },

    methods: {
        ...mapActions('orders', { showHelperOrder: 'show' }),
        ...mapActions('user_orders', { showCustomerOrder: 'show' },),
        ...mapActions('user_orders', ['getOrderDetails']),
        ...mapActions('review', ['get_reviews_from_customers_to_helper']),

        isCancelledDeliveryOrder,

        getDate(date) {
            return date ? date.split(' ')[0] : ''
        },

        async openDeliveryDetailsModal(id) {
            await this.getOrderDetails(id);

            this.$modal.show(UserDashboardDeliveryCardDetailsModal, {
                order: this.delivery
            }, {
                width: 1200,
                height: 'auto',
            }, {
                'before-open': this.$scrollLock.enable,
                'before-close': this.$scrollLock.disable,
            });
            EventBus.$on('acceptDelivery', () => {
                this.shouldAccept = true;
            });
            EventBus.$on('cancelDelivery', () => {
                this.shouldCancel = true;
            });
        },

        async openDeliveryReviewModal () {
          const { data: reviews } = await this.get_reviews_from_customers_to_helper(this.delivery.id);

          this.$modal.show(DeliveryReviews, {
            reviews: reviews ?? [],
          }, {
            width: 800,
            height: 600,
          })
        },
    },

    computed: {
        ...mapGetters('auth', ['isCustomer']),

        ...mapGetters('user_orders', ['detailOrderFormat']),

        getVehicle() {
            const vehicle = this.delivery.vehicle;

            if (vehicle) {
                const slug = vehicle.toLowerCase();
                return {
                    name: vehicle,
                    image: `${ DASHBOARD_URL }/assets/images/vehicles/${ slug }.svg`,
                }
            }
        },
    },

    mounted() {
        this.interval = setInterval(() => {
            const a = new Date(this.delivery.till_auto_reject);
            const b = new Date(dayjs().tz('America/Los_Angeles').format('YYYY-MM-DD HH:mm:ss'));

            const difference = a - b;

            this.timeDiff = difference > 0
                ? convertSecondToTime(difference)
                : null;
        }, 1000);
    },

    destroyed() {
        clearInterval(this.interval);
    }
}
</script>
